import React, { useState } from 'react';

import { observer } from 'mobx-react-lite';
import { useStore } from '@vromo/mobx-react/mobx-react';

import { DropDown, DropDownChoice } from '@vromo/ui/components/DropDown';
import { UnitPicker } from '@vromo/ui/components/UnitPicker';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Input from '@vromo/ui/components/Input';
import convertDistance, { FACTOR } from '@vromo/utils/convertDistance';

import { Condition, conditionDefinitions, Unit, unitProperties } from '../Condition';
import { RootStore } from '../../stores/RootStore';
import EnumInput from './EnumInput';
import { DropDownWrapper, InputWrapper, OuterWrapper } from './styled';

interface ConditionInputProps {
  condition: Condition,
  editCondition: (index: number, property: string, value: string | number, unit?: Unit) => void,
  index: number,
}

const ConditionInput = observer<ConditionInputProps>(function ConditionInput({ index, condition, editCondition }) {
  const { viewStore } = useStore<RootStore>('rootStore');
  const { decimals = 0, type, transformOnBlur, options } = conditionDefinitions.get(condition.property!)!;
  const [inputVal, setInputVal] = useState(
    type === 'distance' && condition.value
      ? convertDistance(condition.value as number * 1000, viewStore.imperialUnits)
        .distance()
        .toFixed(2)
        .toString()
      : condition.value?.toString() || ''
    );

  const unitOptions = unitProperties
    .filter(unit => unit.label && condition.unit && unit.type === condition.unit.type);

  const handleInputBlur = () => {
    let newVal: number | string | undefined = inputVal;
    if (type === 'distance' && newVal) {
      newVal = Number(newVal).toFixed(2)
      setInputVal(newVal?.toString() || '');
      newVal = Number(newVal) / (viewStore.imperialUnits ? FACTOR : 1)
      editCondition(index, condition.property!, newVal, condition.unit!);
    } else if (transformOnBlur) {
      const boundTransformation = transformOnBlur.bind(conditionDefinitions.get(condition.property!)!);
      newVal = boundTransformation(inputVal);
      editCondition(index, condition.property!, newVal, condition.unit!);
      setInputVal(newVal?.toString() || '');
    }
  }

  switch (type) {
    case 'boolean':
      return null;
    case 'enum':
      return <EnumInput
        value={condition.value?.toString()}
        options={options}
        editCondition={(value) => editCondition(index, condition.property!, value.toString())}
      />
    default:
      return (
        <OuterWrapper>
          <InputWrapper>
            <Input
              disabled={!condition.property}
              min="0"
              noTopMargin
              onBlur={handleInputBlur}
              onChange={(e) => setInputVal(e.target.value)}
              step={(10 ** (-decimals)).toFixed(decimals)}
              style={{
                height: 'unset',
                padding: '10px',
                minWidth: 0
              }}
              type="number"
              value={inputVal || ''}
            />
          </InputWrapper>
          <DropDownWrapper>
            {
            !!unitOptions.length &&
              <DropDown
                button={
                  <UnitPicker>
                    {
                      type === 'distance'
                        ? viewStore.imperialUnits
                          ? 'mi'
                          : 'Km'
                        : condition.unit?.label
                      }
                      {
                        unitOptions.length > 1 &&
                          <FontAwesome
                            icon="caret-down"
                            style={{ marginLeft: 10 }}
                          />
                      }
                  </UnitPicker>
                }
              >
                {
                  unitOptions.length > 1
                    ? unitOptions.map(unit =>
                        <DropDownChoice
                          current={condition.unit!.label!}
                          key={unit.label}
                          label={unit.label || ''}
                          onChange={() =>
                            editCondition(index, condition.property!, condition.value as number, unit)
                          }
                          value={unit.label!}
                        />
                      )
                    : null
                }
              </DropDown>
          }
          </DropDownWrapper>
        </OuterWrapper>
      )
  }
});

export default ConditionInput;
